import React from "react";

import { LeftMenuItem } from "./const";
import { LeftMenuLinkItem } from "./MenuItem";
import { AccountData, AccountType } from "../../models/AccountData";
import { FeatureUseLimitCalculator } from "../../models/featureUseLimits/FeatureUseLimitsCalculator";
import {
  FeatureUseStatus,
  OrgFeatures,
} from "../../models/featureUseLimits/FeatureUseLimitsData.const";

interface ILeftMenuMenuGenerationProps {
  to?: string;
  toFunction?: (me: AccountData) => string;
  displayCondition?: (me: AccountData) => boolean;
  icon: string;
  translationKey: string;
}

export const generateLeftMenuProps = (
  menuItem: LeftMenuItem
): ILeftMenuMenuGenerationProps | undefined => {
  switch (menuItem) {
    case LeftMenuItem.adminLocations:
      return {
        to: "/admin/locations",
        icon: "icon-locations",
        translationKey: "facility_management",
      };
    case LeftMenuItem.adminOrganizations:
      return {
        to: "/admin/organizations",
        icon: "icon-organizations",
        translationKey: "organization_management",
      };
    case LeftMenuItem.adminFunctionManagement:
      return {
        to: "/admin/functionManagement",
        icon: "icon-function-management",
        translationKey: "function_management",
      };
    case LeftMenuItem.adminInternalTools:
      return {
        to: "/admin/internalTools",
        icon: "icon-internal-tools",
        translationKey: "internal_tools",
      };
    case LeftMenuItem.adminDeleteGoogleLink:
      return {
        to: "/admin/deleteGoogleLink",
        icon: "icon-trash",
        translationKey: "delete_google_link",
      };
    case LeftMenuItem.adminMassSendEmails:
      return {
        to: "/admin/massSendEmails",
        icon: "icon-email",
        translationKey: "email_users",
      };
    case LeftMenuItem.adminReturnToAdminMain:
      return {
        to: "/admin/organizations/",
        icon: "icon-build",
        translationKey: "return_to_admin_main",
      };
    case LeftMenuItem.adminAccountManagement:
      return {
        toFunction: ({ organization_id }) =>
          `/admin/organizations/${organization_id}/accounts`,
        icon: "icon-user-circle",
        translationKey: "account",
      };
    case LeftMenuItem.adminFindUserAccount:
      return {
        to: "/admin/findUserAccount",
        icon: "icon-find-user",
        translationKey: "find_user",
      };
    case LeftMenuItem.adminInternalLabelsManagement:
      return {
        to: "/admin/internalLabels",
        icon: "icon-internal-labels",
        translationKey: "internal_labels_management",
      };
    case LeftMenuItem.adminErrorLogsManagement:
      return {
        to: "/admin/errorLogs",
        icon: "icon-error-logs",
        translationKey: "error_logs_management",
      };
    case LeftMenuItem.customizedReviewsAnalysis:
      return {
        to: "/admin/customizedReviewsAnalysisPage",
        icon: "icon-custom-reviews",
        translationKey: "customized_reviews_analysis",
      };
    case LeftMenuItem.orgDashboard:
      return {
        toFunction: ({ organization_id: orgId }) =>
          `/dashboard/organization/${orgId}`,
        icon: "icon-dashboard",
        translationKey: "dashboard",
      };
    case LeftMenuItem.orgMassPost:
      return {
        toFunction: ({ organization_id: orgId }) =>
          `/massPost/organization/${orgId}`,
        displayCondition: (me) =>
          FeatureUseLimitCalculator.getFeatureUseStatus(
            OrgFeatures.CAN_USE_MASS_POST,
            me.organization?.commonFeatureUseLimit
          ) !== FeatureUseStatus.CANNOT_USE,
        icon: "icon-mass-post",
        translationKey: "mass_post",
      };
    case LeftMenuItem.orgAnalysisCollections:
      return {
        toFunction: ({ organization_id: orgId }) =>
          `/analysis/organization/${orgId}/collections`,
        icon: "icon-analysis",
        translationKey: "analysis",
      };
    case LeftMenuItem.orgRecommendations:
      return {
        toFunction: ({ organization_id: orgId }) =>
          `/recommendations/organization/${orgId}`,
        icon: "icon-recommendations",
        translationKey: "suggestions",
      };
    case LeftMenuItem.orgRecMessages:
      return {
        toFunction: ({ organization_id: orgId }) =>
          `/recMessages/organization/${orgId}`,
        icon: "icon-rec-messages",
        translationKey: "rec_messages",
      };
    case LeftMenuItem.orgTools:
      return {
        toFunction: ({ organization_id: orgId }) =>
          `/orgTools/organization/${orgId}`,
        icon: "icon-setting",
        translationKey: "tools",
      };
    case LeftMenuItem.facilityList:
      return {
        toFunction: ({ organization_id: orgId }) =>
          orgId ? `/locations/organization/${orgId}` : "/locations",
        icon: "icon-facility-list",
        translationKey: "facility_list",
      };
    case LeftMenuItem.accountSettings:
      return {
        toFunction: ({ id }) => `/account/${id}`,
        icon: "icon-user-circle",
        translationKey: "account",
      };
    case LeftMenuItem.manageLocations:
      return {
        to: "/management/locations",
        icon: "icon-integration",
        translationKey: "integration",
      };
    case LeftMenuItem.locationActivity:
      return {
        to: "/activity/locations",
        icon: "icon-location-activity",
        translationKey: "location_activity",
      };
    case LeftMenuItem.adminLocationAlerts:
      return {
        to: "/admin/locationAlerts",
        icon: "icon-alert",
        translationKey: "location_alerts",
      };
    case LeftMenuItem.dataDownload:
      return {
        toFunction: ({ organization_id: orgId }) =>
          `/data/organization/${orgId}`,
        icon: "icon-data-download",
        translationKey: "data_download",
      };
    case LeftMenuItem.EXPERIMENTAL_organizationFeatures:
      return {
        toFunction: ({ organization_id: orgId }) =>
          `/experimental/organizationFeatures/organization/${orgId}`,
        displayCondition: (me) =>
          me.type === AccountType.ADMIN ||
          FeatureUseLimitCalculator.getFeatureUseStatus(
            OrgFeatures.USE_EXPERIMENTAL_FEATURES,
            me.organization?.commonFeatureUseLimit
          ) !== FeatureUseStatus.CANNOT_USE,
        icon: "icon-experimental",
        translationKey: "EXPERIMENTAL_organization_features",
      };
    default:
      console.log("Unknown menu item:", menuItem);
  }
};

export const converMenuItemToElement = (
  item: LeftMenuItem | undefined,
  me?: AccountData
): React.ReactElement | undefined => {
  if (!item) {
    return;
  }

  const menuItemData = generateLeftMenuProps(item);
  if (!menuItemData || !me) {
    return;
  }

  if (menuItemData.displayCondition && !menuItemData.displayCondition(me)) {
    return;
  }

  if (!menuItemData.to && !menuItemData.toFunction) {
    console.log("No destination for menu item:", item);
    return;
  }

  const destination = (
    menuItemData.toFunction ? menuItemData.toFunction(me) : menuItemData.to
  ) as string;

  return (
    <LeftMenuLinkItem
      key={item}
      to={destination}
      icon={menuItemData.icon}
      translationKey={menuItemData.translationKey}
    />
  );
};
